import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
// src/components/layout/CommandPalette.tsx
// ⌘K / Ctrl-K command palette: jump to any navigation destination the user can
// access. Indexed from the shared nav config (src/config/navigation.ts) and
// filtered by module permissions, so it never drifts from the sidebar.

import { useNavigate } from 'react-router-dom';
import { SearchIcon, CornerDownLeftIcon } from 'lucide-react';
import { flattenDestinations } from '../../config/navigation';
import { useModulePermissions } from '../../hooks/useModulePermissions';

interface CommandPaletteContextValue {
  open: () => void;
  close: () => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export function useCommandPalette(): CommandPaletteContextValue {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) throw new Error('useCommandPalette must be used within CommandPaletteProvider');
  return ctx;
}

export const CommandPaletteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { canRead } = useModulePermissions();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  // Destinations the current user is allowed to see.
  const destinations = useMemo(
    () => flattenDestinations().filter((d) => {
      try { return canRead(d.module); } catch { return true; }
    }),
    [canRead]
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return destinations;
    return destinations.filter(
      (d) => d.name.toLowerCase().includes(q) || d.group.toLowerCase().includes(q)
    );
  }, [query, destinations]);

  // Global ⌘K / Ctrl-K toggle.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setIsOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Reset + focus each time it opens.
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActive(0);
      const t = setTimeout(() => inputRef.current?.focus(), 20);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => { setActive(0); }, [query]);

  const go = useCallback((path: string) => {
    setIsOpen(false);
    navigate(path);
  }, [navigate]);

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (results[active]) go(results[active].path); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
  };

  return (
    <CommandPaletteContext.Provider value={{ open, close }}>
      {children}
      {isOpen && (
        <div
          className="fixed inset-0 z-[60] bg-navy-950/45 flex items-start justify-center p-4 pt-[12vh]"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label="Command menu"
        >
          <div
            className="w-full max-w-lg bg-white rounded-lg shadow-e3 border border-ink-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-4 border-b border-ink-200">
              <SearchIcon className="h-5 w-5 text-ink-400 flex-shrink-0" aria-hidden="true" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onInputKey}
                placeholder="Search pages…"
                aria-label="Search pages"
                className="w-full py-3.5 text-body text-ink-900 placeholder-ink-400 bg-transparent focus:outline-none"
              />
              <kbd className="hidden sm:inline text-meta font-mono text-ink-400 border border-ink-200 rounded px-1.5 py-0.5">Esc</kbd>
            </div>

            <div className="max-h-[52vh] overflow-y-auto py-2">
              {results.length === 0 ? (
                <p className="px-4 py-8 text-center text-body-sm text-ink-500">No pages match “{query}”.</p>
              ) : (
                results.map((d, i) => {
                  const Icon = d.icon;
                  return (
                    <button
                      key={d.path}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => go(d.path)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${
                        i === active ? 'bg-navy-50' : 'hover:bg-ink-50'
                      }`}
                    >
                      <Icon className="h-4 w-4 text-ink-400 flex-shrink-0" />
                      <span className="text-body-sm text-ink-900">{d.name}</span>
                      <span className="text-meta text-ink-400 ml-1">{d.group}</span>
                      {i === active && (
                        <CornerDownLeftIcon className="h-3.5 w-3.5 text-ink-400 ml-auto" aria-hidden="true" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </CommandPaletteContext.Provider>
  );
};
